/**
 * Photos and files attached to records (Phase 2 plan §13, D238). A photo is first **declared** by its
 * id, type, size and SHA-256; only then are the bytes uploaded, straight to storage through a
 * presigned POST (never through the API or a hub). The server accepts exactly the declared bytes for
 * that id — any other file is refused — and then the attachment is **stored**. So a document recorded
 * offline can name its photo (by hash) before the photo itself reaches the server: "no photo, no
 * document" holds, and the photo can never be swapped afterwards.
 */
import { z } from 'zod';

import { isUuidV7 } from '../ids/ids.ts';

/** What a photo is for; it decides who may add and see it (the API checks the permission). */
export const ATTACHMENT_PURPOSES = [
  'ITEM_PHOTO',
  'INVOICE',
  'RECEIPT',
  'DAMAGE',
  'WASTE',
  'SIGNED_NOTE',
  'SUPPLIER_FILE',
  'IMPORT_FILE',
  /** The paper of goods returned to a supplier (an excess returned, W5-3; supplier returns, W6). */
  'RETURN_NOTE',
] as const;
export type AttachmentPurpose = (typeof ATTACHMENT_PURPOSES)[number];

/** Pictures only for now: the phone camera and photos of paper give JPEG, PNG or WebP. */
export const ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AttachmentType = (typeof ATTACHMENT_TYPES)[number];

/** The largest original accepted (devices shrink photos first: see attachmentImageLimits). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export interface AttachmentImageLimits {
  /** The longest side a device sends; a bigger photo is shrunk on the device before upload. */
  readonly maxSidePx: number;
  /** The JPEG quality (0–1) of a photo the device had to shrink or convert. */
  readonly jpegQuality: number;
}

/**
 * How much a device may shrink a photo before sending it (owner, O55). An item's photo is only
 * looked at: 2,000 px, 85% (about 200–600 KB). Every other purpose is a paper or proof — a
 * supplier's invoice, a payment receipt, damaged goods, waste, a signed note — whose small print,
 * handwriting and stamp must stay readable: 3,200 px, 92% (about 1–2.5 MB; a 12 MP phone photo
 * loses only a fifth of its width). A photo already within the limit is sent unchanged.
 */
export function attachmentImageLimits(purpose: AttachmentPurpose): AttachmentImageLimits {
  return purpose === 'ITEM_PHOTO'
    ? { maxSidePx: 2_000, jpegQuality: 0.85 }
    : { maxSidePx: 3_200, jpegQuality: 0.92 };
}

/** WebP versions made by the server from a stored image: a large view and a thumbnail. */
export const ATTACHMENT_VARIANTS = { large: 1_600, thumb: 320 } as const;
export type AttachmentVariant = keyof typeof ATTACHMENT_VARIANTS;

export const AttachmentDeclareSchema = z.strictObject({
  id: z.uuid().refine(isUuidV7, { message: 'UUIDv7 expected' }),
  purpose: z.enum(ATTACHMENT_PURPOSES),
  contentType: z.enum(ATTACHMENT_TYPES),
  byteSize: z.number().int().min(1).max(MAX_ATTACHMENT_BYTES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type AttachmentDeclare = z.infer<typeof AttachmentDeclareSchema>;

/**
 * Where the bytes live in the private bucket: `att/<yyyy>/<mm>/<id>`, the month of the id's own time
 * (a UUIDv7 carries its creation time), so the key never depends on when the upload happens.
 */
export function attachmentObjectKey(id: string): string {
  const millis = Number.parseInt(id.replaceAll('-', '').slice(0, 12), 16);
  const date = new Date(millis);
  const year = String(date.getUTCFullYear());
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `att/${year}/${month}/${id}`;
}

/**
 * Where a device uploads the bytes: `incoming/<id>`, never the stored key. Once the server has
 * checked them it writes them itself under `attachmentObjectKey`, a key no upload link is ever
 * signed for — so a stored photo cannot be replaced, even with an upload link still valid (D260).
 */
export function attachmentUploadKey(id: string): string {
  return `incoming/${id}`;
}

/** The key of a WebP version: `att/<yyyy>/<mm>/<id>.large.webp`. */
export function attachmentVariantKey(id: string, variant: AttachmentVariant): string {
  return `${attachmentObjectKey(id)}.${variant}.webp`;
}

/**
 * The real type of an image from its first bytes (JPEG FF D8 FF, PNG 89 50 4E 47 0D 0A 1A 0A,
 * WebP "RIFF"…"WEBP"), or null. The server compares it with the declared type: a file renamed to
 * .jpg is refused.
 */
export function sniffImageType(bytes: Uint8Array): AttachmentType | null {
  const at = (i: number) => bytes[i] ?? -1;
  if (at(0) === 0xff && at(1) === 0xd8 && at(2) === 0xff) return 'image/jpeg';
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (png.every((b, i) => at(i) === b)) return 'image/png';
  const ascii = (from: number, length: number) =>
    String.fromCharCode(...Array.from(bytes.subarray(from, from + length)));
  if (bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'image/webp';
  return null;
}
